import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Card, Alert, Loader, Button, Input, Select } from '@dreamtree-org/twreact-ui';
import DataTable from '@/components/DataTable';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { CallStatusBadge, GoalFieldsEditor, parseDeliverInfo } from './VoiceCallsPage';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * Call campaigns (`/app-voice-call-campaign`) — batch/scheduled sets of AI calls.
 * Launching materializes each recipient as its own call row (the campaign is
 * bookkeeping, the calls are the truth — services/voice/callOrchestrator.js), so an
 * expanded campaign simply shows its calls, each clickable through to the same call
 * detail page as a one-off.
 */
function CampaignStatusBadge({ status }) {
  const variant = { completed: 'success', running: 'primary', scheduled: 'primary', draft: 'secondary', failed: 'error', canceled: 'secondary' };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status}</Badge>;
}

const RECIPIENTS_PLACEHOLDER = '[\n  {"phone_number": "+15551234567", "dynamic_variables": {"customer_name": "Alice"}},\n  {"phone_number": "+15559876543"}\n]';

function NewCampaignDialog({ onClose, onCreated }) {
  const { fetchApi } = useAppApi();
  const [name, setName] = useState('');
  const [purpose, setPurpose] = useState('collect');
  const [fields, setFields] = useState([{ name: '', type: 'string', description: '' }]);
  const [deliverText, setDeliverText] = useState('');
  const [recipientsText, setRecipientsText] = useState('');
  const [concurrency, setConcurrency] = useState(2);
  const [scheduledAt, setScheduledAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  let submitLabel = scheduledAt ? 'Create (scheduled)' : 'Create draft';
  if (busy) submitLabel = 'Creating…';

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      let recipients;
      try {
        recipients = JSON.parse(recipientsText);
      } catch {
        throw new Error('Recipients must be valid JSON — an array of {phone_number, dynamic_variables?}');
      }
      const goalFields = fields.filter((f) => f.name.trim());
      await fetchApi('voiceCampaignCreate', {
        name,
        purpose,
        goal_schema: (purpose !== 'deliver' && goalFields.length) ? { fields: goalFields } : null,
        deliver_payload_template: purpose !== 'collect' ? parseDeliverInfo(deliverText) : null,
        recipients,
        concurrency_limit: Number(concurrency) || 2,
        scheduled_at: scheduledAt ? new Date(scheduledAt).toISOString() : null,
      });
      onCreated();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="p-3 space-y-3 border-primary-300">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">New call campaign</h2>
        <button className="text-sm text-secondary-500" onClick={onClose}>Close</button>
      </div>
      {err && <Alert variant="error" title="Could not create campaign">{err}</Alert>}
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          <Input label="Name" value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <Select
          label="Purpose"
          options={[
            { value: 'collect', label: 'Collect information' },
            { value: 'deliver', label: 'Deliver information' },
            { value: 'both', label: 'Collect + deliver' },
          ]}
          value={purpose}
          onChange={(v) => setPurpose(v)}
        />
      </div>
      {purpose !== 'deliver' && <GoalFieldsEditor fields={fields} setFields={setFields} />}
      {purpose !== 'collect' && (
        <div>
          <label className="block text-sm font-medium mb-1">Information to deliver (key: value per line — per-recipient dynamic_variables override these)</label>
          <textarea
            className="w-full min-h-[60px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
            value={deliverText}
            onChange={(e) => setDeliverText(e.target.value)}
          />
        </div>
      )}
      <div>
        <label className="block text-sm font-medium mb-1">Recipients (JSON array, max 500)</label>
        <textarea
          className="w-full min-h-[110px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm font-mono"
          placeholder={RECIPIENTS_PLACEHOLDER}
          value={recipientsText}
          onChange={(e) => setRecipientsText(e.target.value)}
        />
      </div>
      <div className="grid grid-cols-3 gap-3 items-end">
        <Input label="Concurrent calls (1-10)" type="number" value={concurrency} onChange={(e) => setConcurrency(e.target.value)} />
        <div>
          <label className="block text-sm font-medium mb-1">Schedule (optional)</label>
          <input
            type="datetime-local"
            className="border rounded-md px-2 py-1.5 text-sm bg-[var(--app-surface)] w-full"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
          />
        </div>
        <div className="text-right">
          <Button onClick={submit} disabled={busy || !name.trim() || !recipientsText.trim()}>
            {submitLabel}
          </Button>
        </div>
      </div>
      <p className="text-xs text-secondary-500">A draft campaign dials nothing until you press Launch; a scheduled one launches itself when due.</p>
    </Card>
  );
}

/** Expanded-row content: this campaign's calls, fetched on first open. */
function CampaignCalls({ row: campaign }) {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [detail, setDetail] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    fetchApi('voiceCampaignShow', campaign.id)
      .then(setDetail)
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message));
  }, [campaign.id]);

  if (err) return <Alert variant="error" title="Error">{err}</Alert>;
  if (!detail) return <Loader text="Loading campaign calls…" />;
  return (
    <table className="w-full text-sm">
      <thead className="text-left text-xs uppercase text-secondary-500">
        <tr><th className="py-1">To</th><th className="py-1">Status</th><th className="py-1">Result</th></tr>
      </thead>
      <tbody>
        {detail.calls.map((call) => (
          <tr key={call.id} className="cursor-pointer hover:underline" onClick={() => nav(`/app-voice-call/${call.id}`)}>
            <td className="py-1">{call.to_number}</td>
            <td className="py-1"><CallStatusBadge status={call.status} /></td>
            <td className="py-1 max-w-[320px] truncate">{call.transcript_summary || call.error || '—'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CampaignActions({ campaign, onAction }) {
  const { fetchApi } = useAppApi();
  const [err, setErr] = useState('');
  const act = async (endpoint) => {
    try {
      await fetchApi(endpoint, campaign.id);
      onAction();
    } catch (ex) {
      setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message);
    }
  };
  return (
    <Can module="calls" action="dial">
      <div className="flex gap-2" title={err || undefined}>
        {['draft', 'scheduled'].includes(campaign.status) && <Button size="sm" onClick={() => act('voiceCampaignLaunch')}>Launch</Button>}
        {['draft', 'scheduled', 'running'].includes(campaign.status) && <Button size="sm" variant="secondary" onClick={() => act('voiceCampaignCancel')}>Cancel</Button>}
      </div>
    </Can>
  );
}

function campaignColumns(onAction) {
  return [
    { key: 'name', label: 'Name', render: (c) => <span className="font-medium text-primary-600">{c.name}</span> },
    { key: 'status', label: 'Status', searchable: false, render: (c) => <CampaignStatusBadge status={c.status} /> },
    { key: 'total_calls', label: 'Calls', searchable: false },
    { key: 'finished_calls', label: 'Progress', searchable: false, render: (c) => `${c.finished_calls} ok / ${c.failed_calls} failed` },
    { key: 'scheduled_at', label: 'Scheduled', searchable: false, render: (c) => <span className="text-secondary-500">{c.scheduled_at ? new Date(c.scheduled_at).toLocaleString() : '—'}</span> },
    {
      key: 'row_actions',
      label: 'Actions',
      sortable: false,
      searchable: false,
      render: (c) => <CampaignActions campaign={c} onAction={onAction} />,
    },
  ];
}

export default function VoiceCampaignsPage() {
  const { fetchApi } = useAppApi();
  const [campaigns, setCampaigns] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const load = () => {
    fetchApi('voiceCampaignList')
      .then((rows) => setCampaigns(Array.isArray(rows) ? rows : []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const filteredCampaigns = applicationId
    ? campaigns.filter(c => c.application_id == null || c.application_id === Number(applicationId))
    : campaigns;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Call Campaigns"
        subtitle={<>Batch and scheduled AI call runs — each recipient becomes an audited call of its own.</>}
        actions={(
          <Can module="calls" action="dial">
            <Button size="sm" onClick={() => setShowNew((v) => !v)}>New campaign</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Could not load">{err}</Alert>}
      {showNew && <NewCampaignDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}

      <DataTable
        data={filteredCampaigns}
        columns={campaignColumns(load)}
        loading={loading}
        emptyText="No campaigns yet."
        search={{ placeholder: 'Search campaigns…', keys: ['name'] }}
        onReload={load}
        hasDetails
        DetailsComponent={CampaignCalls}
        toolbar={(
          <div className="w-48 sm:w-56">
            <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
          </div>
        )}
      />
    </PageShell>
  );
}
